/**
 * Module dependencies.
 */

import { ReactElement, useMemo } from 'react';
import { StepKey } from 'src/types/advisor';
import { Svg, Type, icons } from 'src/components/core';
import { chemicalTypes } from 'src/core/content-config/model-ranges';
import { color, units } from '@untile/react-components';
import { ifProp, theme } from 'styled-tools';
import {
  stepsKeys,
  stepsWithTranslationSidebar
} from 'src/core/advisor-config/steps-options';

import includes from 'lodash/includes';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  onClick: () => void;
  pageKey: StepKey;
  value?: string | null;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: grid;
  grid-column-gap: ${units(1)};
  grid-template-areas:
    'icon name'
    '.    value';
  grid-template-columns: ${units(3)} 1fr;
  transition: opacity ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    opacity: 0.8;
  }
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg).attrs({ size: units(3) })<{ isUndefined: boolean }>`
  grid-area: icon;

  ${ifProp(
    'isUndefined',
    css`
      color: ${color('grey300')};
    `
  )}
`;

/**
 * `Name` styled component.
 */

const Name = styled(Type.Paragraph)`
  color: ${color('grey500')};
  grid-area: name;
`;

/**
 * `Value` styled component.
 */

const Value = styled(Type.Paragraph)<{ isUndefined: boolean }>`
  font-weight: 700;
  grid-area: value;

  ${ifProp(
    'isUndefined',
    css`
      color: ${color('grey400')};
    `
  )}
`;

/**
 * `SidebarStep` component.
 */

const SidebarStep = (props: Props): ReactElement => {
  const { className, onClick, pageKey } = props;
  const { translate } = useTranslate();
  const name = translate(`advisor:steps.${pageKey}.sidebar`);
  const value = useMemo(() => {
    if (!props.value) {
      return null;
    }

    if (includes(stepsWithTranslationSidebar, pageKey)) {
      if (pageKey === stepsKeys.typeChemical) {
        if (!includes(Object.values(chemicalTypes), props.value)) {
          return props.value;
        }
      }

      return translate(`advisor:labels.${props.value}`);
    }

    return props.value;
  }, [pageKey, props?.value, translate]);

  return (
    <Wrapper
      className={className}
      onClick={onClick}
    >
      <Icon
        icon={value ? icons.check : icons.remove}
        isUndefined={!value}
      />

      <Name>{name}</Name>

      <Value isUndefined={!value}>
        {value ?? translate('common:labels.undefined')}
      </Value>
    </Wrapper>
  );
};

/**
 * Export `SidebarStep` component.
 */

export default SidebarStep;
